import React, {
  useState,
  useCallback,
  Children,
  isValidElement,
  cloneElement,
  useRef,
  useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import classNames from 'classnames';

import { Icons } from '../icons';
import { useTranslate } from '../../hooks';
import Badge from '@mui/material/Badge';
import { colors } from '../../helpers/colors';

const Tab = ({
  set,
  title,
  index,
  isActive = true,
  className = '',
  handleTab,
  onCustomClick,
  viewBadge,
  count,
  width,
  currentTab
}) => {
  const [search, setSearch] = useSearchParams();

  return (
    <li
      className={classNames(
        `py-5 w-fit whitespace-nowrap text-center cursor-pointer hover:text-primary transition-300 ${className}`,
        {
          'border-b-[3px] border-primary text-primary font-bold': isActive,
        },
      )}
      onClick={() => {
        handleTab?.(index || 1, title);
        onCustomClick?.(currentTab);

        if ((+search.get('page') > 0 || search.get('search')) && !isActive) {
          setSearch((prev) => ({
            ...prev,
            page: 0,
            search: '',
          }));
        }
      }}
    >
      {count != 0 ? (
        <Badge
          badgeContent={count}
          color="secondary"
          sx={{
            '& .MuiBadge-badge': {
              top: '-5px',
              right: '-5px',
              transform: 'scale(1) translate(50%, -50%)',
              transformOrigin: '100% 0%',
              background: colors?.primary
            }
          }}
        >
          {title}
        </Badge>
      ) : (
        title
      )}
    </li>
  );
};

const Tabs = ({ children, className = '', activeTab = 1, onTabChange, refresh }) => {
  const [tabIndex, setTabIndex] = useState(activeTab);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const containerRef = useRef(null);
  const [tabPositions, setTabPositions] = useState([]);
  const [currentScroll, setCurrentScroll] = useState(0);

  const { dir } = useTranslate();
  useEffect(() => {
    setTabIndex(activeTab)
  }, [activeTab, refresh]);
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    updateTabPositions();

    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    if (!prevButtonRef.current || !nextButtonRef.current) return;

    prevButtonRef.current.style.display =
      container.scrollWidth > container.clientWidth ? 'block' : 'none';
    nextButtonRef.current.style.display =
      container.scrollWidth > container.clientWidth ? 'block' : 'none';

    if (dir === 'ltr') {
      if (scrollLeft <= 0) prevButtonRef.current.style.cursor = 'not-allowed';
      else prevButtonRef.current.style.cursor = 'pointer';
      if (scrollLeft + 10 >= maxScrollLeft)
        nextButtonRef.current.style.cursor = 'not-allowed';
      else nextButtonRef.current.style.cursor = 'pointer';
    } else {
      if (
        maxScrollLeft - 1 === Math.abs(parseInt(scrollLeft)) ||
        maxScrollLeft - 1 === Math.abs(parseInt(scrollLeft)) + 1
      ) {
        nextButtonRef.current.style.cursor = 'not-allowed';
        prevButtonRef.current.style.cursor = 'pointer';
      } else if (Math.abs(parseInt(scrollLeft)) === 0) {
        nextButtonRef.current.style.cursor = 'pointer';
        prevButtonRef.current.style.cursor = 'not-allowed';
      } else if (maxScrollLeft > Math.abs(parseInt(scrollLeft)) + 1) {
        nextButtonRef.current.style.cursor = 'pointer';
        prevButtonRef.current.style.cursor = 'pointer';
      }
    }
  }, [dir]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    handleScroll();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleTab = (index) => {
    onTabChange?.(index);
    setTabIndex(index);

    const container = containerRef.current;
    if (!container) return;

    const tabToScroll = tabPositions[index - 1];
    const containerWidth = container.clientWidth;

    if (dir === 'rtl') {
      if (tabToScroll?.right > currentScroll + containerWidth) {
        const scrollLeft = Math.min(
          tabToScroll?.left,
          tabToScroll?.right - containerWidth,
        );
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      } else if (tabToScroll?.left - 50 < currentScroll) {
        const scrollLeft = Math.min(0, tabToScroll?.left - containerWidth / 2);
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    } else {
      // For LTR layout, keep the existing scrolling logic
      if (tabToScroll?.left - 50 < currentScroll) {
        const scrollLeft = Math.max(0, tabToScroll?.left - containerWidth / 2);
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      } else if (tabToScroll?.right > currentScroll + containerWidth) {
        const scrollLeft = Math.max(
          tabToScroll?.left,
          tabToScroll?.right - containerWidth,
        );
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleWheelScroll = (e, direction) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth;
    const newScrollLeft =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scroll({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  const updateTabPositions = () => {
    const container = containerRef.current;
    if (!container) return;

    const tabElements = Array.from(container.children);
    const positions = tabElements.map((element, index) => {
      const left = element.offsetLeft;
      const right = left + element.offsetWidth;
      return {
        left,
        right,
        index,
      };
    });

    const scrollLeft = container.scrollLeft;

    setTabPositions(positions);
    setCurrentScroll(scrollLeft);
  };

  const getTabName = useCallback((tabName) => {
    return tabName?.split(' ').join('_');
  }, []);

  const getIsActive = (index) => {
    return index + 1 === tabIndex;
  };

  if (!children) return null;

  return (
    <div className="relative flex flex-col justify-between w-full rounded-2xl bg-white">
      <ul className="flex items-center shadow-tabs gap-x-2 px-4 mb-2">
        <li
          className="px-1"
          ref={prevButtonRef}
          onClick={(e) =>
            handleWheelScroll(e, dir === 'ltr' ? 'left' : 'right')
          }
        >
          <Icons
            name="chevron"
            className={classNames('transform text-primary', {
              '-rotate-90': dir === 'rtl',
              'rotate-90': dir === 'ltr',
            })}
          />
        </li>

        <ul
          ref={containerRef}
          className={`flex gap-x-5 overflow-x-auto thin-scroll ${className}`}
          onWheel={(e) => handleWheelScroll(e, e.deltaY > 0 ? 'right' : 'left')}
        >
          {Children.map(children, (child, index) => {
            if (!isValidElement(child)) return null;

            return (
              <Tab
                {...child.props}
                isActive={getIsActive(index)}
                currentTab={tabIndex}
                index={index + 1}
                handleTab={handleTab}
              />
            );
          })}
        </ul>

        <li
          className="px-1"
          ref={nextButtonRef}
          onClick={(e) =>
            handleWheelScroll(e, dir === 'ltr' ? 'right' : 'left')
          }
        >
          <Icons
            name="chevron"
            className={classNames('transform text-primary', {
              'rotate-90': dir === 'rtl',
              '-rotate-90': dir === 'ltr',
            })}
          />
        </li>
      </ul>

      {Children.map(children, (child, index) => {
        if (!isValidElement(child)) return null;

        return (
          <div className="px-5">
            {isValidElement(child.props.children) &&
              getIsActive(index, getTabName(child.props.title)) &&
              cloneElement(child.props.children)}
          </div>
        );
      })}
    </div>
  );
};

export { Tabs, Tab };
